import { useTheme } from '@emotion/react';
import {
  CircularProgress,
  FormHelperText,
  Grid,
  Typography
} from '@mui/material';
import { formatMoneyAmount } from 'framework/base/components/AryNumberFormatter';
import AryWrapper from 'framework/base/components/AryWrapper';
import {
  GET_ACCOUNT_INFORMATION,
  GET_ACCOUNT_UPDATE_STATUS
} from 'framework/base/constants/aryGraphqlSchemas';
import useAryCalculationState from 'framework/base/hooks/useAryCalculationState';
import { useAryLazyQuery } from 'framework/base/hooks/useAryGraphql';
import useShowCurrency from 'framework/base/hooks/useShowCurrency';
import { getMoonUtcTime } from 'framework/base/utils/Utils';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';

const AccountStatus = forwardRef(
  (
    {
      accountNumber,
      currencyCode,
      accountTitles = [],
      multiAccount,
      isCalculationDoneHandler,
      height,
      message
    },
    ref
  ) => {
    const showCurrency = useShowCurrency();
    const theme = useTheme();
    const { t } = useFrameworkTranslation();
    const [currentAccountTitle, setCurrentAccountTitle] = useState('');
    const [isCalculationDone, setIsCalculationDone] = useState(false);
    const [isForceCalculate, setIsForceCalculate] = useState(false);

    const [
      getAccountInformation,
      {
        data: getAccountInformationQueryData,
        loading: getAccountInformationQueryLoading,
        refetch: getAccountInformationQueryRefetch
      }
    ] = useAryLazyQuery({
      gqlConfig: GET_ACCOUNT_INFORMATION,
      items: `
          accountBalances {
            currency
            balance
            creditLimit
            hasUnlimitedLimit
            isDebtor
          }
        `
    });

    useImperativeHandle(ref, () => ({
      getAccountInformation: () => {
        getAccountInformationQueryRefetch();
        setIsForceCalculate(!isForceCalculate);
      }
    }));

    useEffect(() => {
      if (accountNumber) {
        if (multiAccount) {
          const accountNumberLength = accountNumber?.length;
          if (
            accountNumberLength === 1 ||
            (accountNumberLength > 1 && !currentAccountTitle)
          ) {
            getAccountInformation({
              accountNumber: accountNumber[0],
              fromDate: null,
              toDate: getMoonUtcTime(new Date())
            });
            setCurrentAccountTitle(accountTitles[0]);
          }
        } else {
          getAccountInformation({
            accountNumber,
            fromDate: null,
            toDate: getMoonUtcTime(new Date())
          });
        }
      }
    }, [multiAccount ? JSON.stringify(accountNumber) : accountNumber]);

    const [isCalculateDone] = useAryCalculationState({
      gqlConfig: GET_ACCOUNT_UPDATE_STATUS,
      selectedId: Array.isArray(accountNumber)
        ? accountNumber[0]
        : accountNumber,
      forceCalculate: isForceCalculate
    });

    useEffect(() => {
      if (isCalculateDone !== undefined) {
        setIsCalculationDone(isCalculateDone);
        isCalculationDoneHandler(false);
        if (isCalculateDone === true) {
          getAccountInformationQueryRefetch();
          isCalculationDoneHandler(true);
        }
      }
    }, [isCalculateDone]);

    const data =
      (!getAccountInformationQueryLoading ||
        getAccountInformationQueryData?.accountBalances?.length) &&
      getAccountInformationQueryData?.accountBalances.find(
        (obj) => obj.currency === showCurrency({ code: currencyCode })
      );

    return (
      <>
        <AryWrapper
          backgroundColor={theme.wrapper.grey50.backgroundColor}
          height={height || '2rem'}
          padding="0.375rem .5rem !important"
          style={{
            alignContent: 'center'
          }}
        >
          {!!accountNumber && !!currencyCode ? (
            <Grid container>
              <Grid item xs={6} display="flex" gap={2}>
                <Typography variant="h7Bold" color={theme.text.secondary.color}>
                  {t('Inventory')}:
                </Typography>
                <Typography
                  variant="h7Bold"
                  color={
                    data
                      ? data?.isDebtor
                        ? theme.text.red.color
                        : theme.text.green.color
                      : theme.text.secondary.color
                  }
                >
                  {data
                    ? `${formatMoneyAmount(data?.balance)} ${data?.currency}`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} display="flex" gap={2}>
                <Typography variant="h7Bold" color={theme.text.secondary.color}>
                  {t('Credit Limit')}:
                </Typography>
                <Typography variant="h7Bold">
                  {data
                    ? data?.hasUnlimitedLimit
                      ? t('Unlimited')
                      : formatMoneyAmount(data?.creditLimit)
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid item>
              <Typography variant="c3Bold" color={theme.text.secondary.color}>
                {message}
              </Typography>
            </Grid>
          )}
        </AryWrapper>
        {isCalculationDone === false && !!accountNumber && !!currencyCode && (
          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '.25rem' }}>
            <CircularProgress
              style={{
                width: '.7rem',
                height: '.7rem',
                alignContent: 'center'
              }}
            />
            <FormHelperText>{t('CalculationBalance')}</FormHelperText>
          </div>
        )}
      </>
    );
  }
);

export default AccountStatus;
